/**
 * grass_driver.ts — the grass layer's per-frame host (2026-09-25, visual-realism R2).
 *
 * Keeps EnvironmentModule's side to three calls (update / reset / dispose): it creates the layer on
 * first use in the Commons, follows the tier flag (`grassLayer`), hides it off the Commons, in space
 * and at vehicle speed (the field cannot follow 150 m/s — and blades are a blur there anyway), wires
 * the world samplers (grass_source.ts) per seed, invalidates the field under a chunk that was
 * rebuilt (re-LOD, a restamp) and hands the layer the camera and the render anchor.
 */
import * as THREE from 'three/webgpu';
import { uniform } from 'three/tsl';
import { GrassLayer } from './grass_layer.js';
import { makeGrassSource } from './grass_source.js';
import { GRASS_FIELD_REACH_M, GRASS_TEXEL_M, type GrassFieldSource } from './grass_field.js';
import { turfColorAt, linearToSrgbByte } from './turf_core.js';
import type { GrassLayerTier } from './grass_params.js';
import { windVector, windPrevDt } from '../wind.js';
import { trampleTexture, trampleBoundsNode } from '../foliage_trample.js';
import { renderAnchor } from '../local_origin.js';
import { CHUNK_SIZE } from '../../config.js';
import { groundKeepOutVersion, groundKeepOutChangesSince, groundKeepOutsNear } from '../ground_keepout.js';

/** Above this camera speed (m/s) the layer hides — a vehicle outruns the field. */
export const GRASS_MAX_SPEED = 40;

export interface GrassHost {
  scene: THREE.Scene;
  /** Is the player in the Commons, on the ground (not in space)? and its seed + sea level. */
  commons(): { seed: string; sea: number } | null;
  tier(): GrassLayerTier;
  /** The resident chunk mesh under (cx, cz), or undefined. */
  chunkMesh(cx: number, cz: number): THREE.Object3D | undefined;
  /** The resident chunk's grid resolution (segments per side) — how `groundAt` finds a vertex. */
  chunkSeg?(cx: number, cz: number): number | undefined;
  /** Placed-structure keep-out discs reaching a rect. */
  structures(x0: number, z0: number, x1: number, z1: number): Array<{ x: number; z: number; r2: number }>;
  /** The camera's ABSOLUTE position (null before a camera exists). */
  cameraAbs(out: THREE.Vector3): THREE.Vector3 | null;
  /** Pre-compile a new object's pipelines (warm-then-show). */
  warm?(o: THREE.Object3D): void;
  sunDir: unknown; night: unknown;
}

export class GrassDriver {
  layer: GrassLayer | null = null;
  private src: (GrassFieldSource & { clearCache(): void }) | null = null;
  private seed = '';
  // PERF (shader sweep C, 2026-10-06): keyed by a NUMBER (chunk indices packed), not a template string per chunk per frame.
  private readonly seen = new Map<number, THREE.Object3D | undefined>();
  private readonly cam = new THREE.Vector3();
  private readonly last = { x: 0, z: 0, t: 0 };
  private speed = 0;
  private density = 1;
  /** The ground keep-out's version the field was last filled against (lib/ground_keepout.ts). */
  private keepV = 0;
  readonly budgetMs = { value: 1.5 };
  /** Probe diagnostics: the last camera (absolute) and whether its chunk was resident. */
  readonly diag = { camX: 0, camZ: 0, camChunkReady: false, updates: 0, keepOutChanges: 0 };

  constructor(private readonly host: GrassHost) {}

  update(): void {
    const tier = this.host.tier();
    const cm = this.host.commons();
    if (tier === 'off' || !cm) {
      if (this.layer) { if (tier === 'off') { this.layer.dispose(); this.layer = null; } else this.layer.setVisible(false); }
      return;
    }
    if (!this.layer) {
      this.layer = new GrassLayer(this.host.scene, tier, {
        wind: uniform(windVector), windDt: windPrevDt, trample: trampleTexture(), trampleBounds: trampleBoundsNode,
        sunDir: this.host.sunDir, night: this.host.night,
      });
      this.layer.setDensity(this.density);
      for (const o of this.layer.objects) this.host.warm?.(o);
    }
    if (cm.seed !== this.seed || !this.src) {
      this.seed = cm.seed;
      this.src = makeGrassSource({
        seed: cm.seed, sea: cm.sea,
        chunkReady: (cx, cz) => this.host.chunkMesh(cx, cz) !== undefined,
        structures: (x0, z0, x1, z1) => this.host.structures(x0, z0, x1, z1),
        groundAt: (x, z, out) => this.groundAt(x, z, out),
        keepOuts: (x0, z0, x1, z1) => groundKeepOutsNear(x0, z0, x1, z1),
      });
      this.layer.resetField();
      this.seen.clear();
    }
    if (tier !== this.layer.tier) { this.layer.setTier(tier); for (const o of this.layer.objects) this.host.warm?.(o); }
    const c = this.host.cameraAbs(this.cam);
    if (!c) return;
    this.diag.camX = c.x; this.diag.camZ = c.z; this.diag.updates++;
    this.diag.camChunkReady = this.host.chunkMesh(Math.floor(c.x / CHUNK_SIZE), Math.floor(c.z / CHUNK_SIZE)) !== undefined;
    // camera speed (smoothed) — the vehicle gate
    const now = performance.now();
    if (this.last.t > 0) {
      const dt = Math.max(1e-3, (now - this.last.t) / 1000);
      const v = Math.hypot(c.x - this.last.x, c.z - this.last.z) / dt;
      this.speed += (Math.min(v, 400) - this.speed) * Math.min(1, dt * 4);
    }
    this.last.x = c.x; this.last.z = c.z; this.last.t = now; // in place (it was a fresh object every frame)
    const fast = this.speed > GRASS_MAX_SPEED;
    this.layer.setVisible(!fast);
    if (fast) return;
    // a chunk rebuilt under the window (re-LOD, restamp) moves the ground: refill its tiles
    const r = GRASS_FIELD_REACH_M + 8;
    for (let cz = Math.floor((c.z - r) / CHUNK_SIZE); cz <= Math.floor((c.z + r) / CHUNK_SIZE); cz++) {
      for (let cx = Math.floor((c.x - r) / CHUNK_SIZE); cx <= Math.floor((c.x + r) / CHUNK_SIZE); cx++) {
        const key = (cx + 32768) * 65536 + (cz + 32768); // |chunk index| < 32768 (a 2048 km world is ±4,000 chunks)
        const m = this.host.chunkMesh(cx, cz);
        if (this.seen.has(key) && this.seen.get(key) === m) continue;
        const had = this.seen.get(key);
        this.seen.set(key, m);
        if (had !== undefined && had !== m) this.layer.field.invalidate(cx * CHUNK_SIZE, cz * CHUNK_SIZE, (cx + 1) * CHUNK_SIZE, (cz + 1) * CHUNK_SIZE);
      }
    }
    if (this.seen.size > 64) this.seen.clear();
    // THE GROUND KEEP-OUT (2026-10-02): a building that assembled (or came down) after its grass was filled — refill
    // only the tiles it touched; nothing grows inside a building or an interior (lib/ground_keepout.ts).
    const kv = groundKeepOutVersion();
    if (kv !== this.keepV) {
      const ch = groundKeepOutChangesSince(this.keepV);
      this.keepV = kv;
      const R = GRASS_FIELD_REACH_M + 16, wx0 = c.x - R, wz0 = c.z - R, wx1 = c.x + R, wz1 = c.z + R;
      if (ch === null) this.layer.field.invalidate(wx0, wz0, wx1, wz1);
      else for (const q of ch) {
        if (q.x1 < wx0 || q.x0 > wx1 || q.z1 < wz0 || q.z0 > wz1) continue;
        this.layer.field.invalidate(Math.max(q.x0, wx0), Math.max(q.z0, wz0), Math.min(q.x1, wx1), Math.min(q.z1, wz1));
      }
      this.diag.keepOutChanges++;
    }
    this.layer.update(c, renderAnchor(), this.src, this.budgetMs.value);
  }

  /**
   * The GROUND's climate channel at ABSOLUTE (x, z), read off the resident chunk mesh's own vertex data:
   * [gTint.r, gTint.g, gTint.b, gTint.w (dryness), gMask.z (wetness)], bilinear between the chunk's grid
   * vertices (row-major from the chunk's min corner, `(seg + 1)²` of them before the skirt — the layout
   * EnvironmentModule's `groundRulesAt` reads). These are exactly the inputs the ground's turf layer
   * shades with (lib/wetness.ts → turf_color.turfColorAtNode), so the blade takes the turf's colour
   * (lib/grass/turf_core.ts). False where no v2 chunk data is resident (the rule then falls back).
   */
  groundAt(x: number, z: number, out: Float32Array): boolean {
    const cx = Math.floor(x / CHUNK_SIZE), cz = Math.floor(z / CHUNK_SIZE);
    const seg = this.host.chunkSeg?.(cx, cz);
    const mesh = this.host.chunkMesh(cx, cz) as THREE.Mesh | undefined;
    if (!seg || !mesh || !(mesh as { geometry?: unknown }).geometry) return false;
    const g = mesh.geometry as THREE.BufferGeometry;
    const tA = g.getAttribute('gTint'), mA = g.getAttribute('gMask');
    const n = seg + 1;
    if (!tA || !mA || tA.count < n * n || mA.count < n * n) return false;
    const cell = CHUNK_SIZE / seg;
    const fx = Math.min(seg, Math.max(0, (x - cx * CHUNK_SIZE) / cell)), fz = Math.min(seg, Math.max(0, (z - cz * CHUNK_SIZE) / cell));
    const c0 = Math.min(seg - 1, Math.floor(fx)), r0 = Math.min(seg - 1, Math.floor(fz));
    const ax = fx - c0, az = fz - r0;
    const i00 = r0 * n + c0, i10 = i00 + 1, i01 = i00 + n, i11 = i01 + 1;
    const w00 = (1 - ax) * (1 - az), w10 = ax * (1 - az), w01 = (1 - ax) * az, w11 = ax * az;
    out[0] = tA.getX(i00) * w00 + tA.getX(i10) * w10 + tA.getX(i01) * w01 + tA.getX(i11) * w11;
    out[1] = tA.getY(i00) * w00 + tA.getY(i10) * w10 + tA.getY(i01) * w01 + tA.getY(i11) * w11;
    out[2] = tA.getZ(i00) * w00 + tA.getZ(i10) * w10 + tA.getZ(i01) * w01 + tA.getZ(i11) * w11;
    out[3] = tA.getW(i00) * w00 + tA.getW(i10) * w10 + tA.getW(i01) * w01 + tA.getW(i11) * w11;
    out[4] = mA.getZ(i00) * w00 + mA.getZ(i10) * w10 + mA.getZ(i01) * w01 + mA.getZ(i11) * w11;
    return Number.isFinite(out[0]) && Number.isFinite(out[3]) && Number.isFinite(out[4]);
  }

  /**
   * Probe read (`__grass.turf`): the ground's channel at (x, z), the ONE rule's colour over it (sRGB bytes) and the
   * colour the field baked into the texel there (sRGB bytes, null while unfilled) — the three must agree.
   */
  turfAt(x: number, z: number): { ground: number[]; rule: number[]; field: number[] | null } | null {
    const g = new Float32Array(5);
    if (!this.groundAt(x, z, g)) return null;
    const rule = (turfColorAt(g[0], g[1], g[2], g[3], g[4], [0, 0, 0]) as number[]).map(linearToSrgbByte);
    const f = this.layer?.field;
    let field: number[] | null = null;
    if (f && f.filledAt(x, z)) { const p = f.texel(Math.floor(x / GRASS_TEXEL_M), Math.floor(z / GRASS_TEXEL_M))[3]; field = [Math.floor(p / 65536), Math.floor(p / 256) % 256, p % 256]; }
    return { ground: Array.from(g), rule, field };
  }

  /** World left / changed: the field forgets everything (the layer stays built). */
  reset(): void { this.layer?.resetField(); this.src?.clearCache(); this.seen.clear(); this.layer?.setVisible(false); }
  setDensity(v: number): void { this.density = v; this.layer?.setDensity(v); }
  get cameraSpeed(): number { return this.speed; }
  dispose(): void { this.layer?.dispose(); this.layer = null; this.src = null; this.seen.clear(); }
}
